/**
 * Shared error shaping for the job close flows (full closing survey and quick
 * close). The server refuses a close with a JSON body of `{ error: '...' }`
 * plus a status, and both flows need to render that reason inside the open
 * dialog rather than dropping a flash alert behind the modal backdrop.
 */

export const CLOSE_JOB_PERMISSION_HELP =
  'You do not have permission to close jobs at this practice. Ask the practice owner to change your role, or call 1-800-799-2644.';

export interface CloseJobError {
  /** The message the server sent back, or a generic fallback. */
  message: string;
  /** Extra plain-language guidance, currently only for a 403. */
  help?: string;
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      error?: string;
      errors?: string[] | string;
    };
  };
}

export const buildCloseJobError = (message: string, status?: number): CloseJobError => ({
  message,
  ...(status === 403 ? { help: CLOSE_JOB_PERMISSION_HELP } : {})
});

/**
 * Pull the server's message and status off a rejected axios request. Falls back
 * to the supplied copy when the failure carries no JSON body (network drop,
 * timeout, thrown Error).
 */
export const extractCloseJobError = (error: unknown, fallback: string): CloseJobError => {
  const response = (error as AxiosLikeError | null)?.response;
  const data = response?.data;

  let message = fallback;
  if (data?.error) {
    message = data.error;
  } else if (data?.errors) {
    message = Array.isArray(data.errors) ? data.errors.join(', ') : String(data.errors);
  }

  return buildCloseJobError(message, response?.status);
};
